import { VirtualElement } from '@popperjs/core'
import { Draggable } from 'components/Draggable'
import React from 'react'
import { usePopper } from 'react-popper'
import styled from 'styled-components'

import { VizTooltipContainer } from './VizTooltipContainer'

const PopperContainer = styled.div`
  position: relative;
  z-index: 999;
  flex-grow: 1;
`

/**
 * A temporary generic wrapper component for using the popper tooltip component.
 * To be replaced by Gecko UI's tooltip component in the future when standardized.
 *
 * This should be used for use cases where the tooltip is NOT hovering over an SVG element.
 */
export const VizTooltip: React.FC<{
  isVisible: boolean
  isFrozen?: boolean
  children: React.ReactNode
}> = ({ isVisible, isFrozen, children }) => {
  const [domRect, setDomRect] = React.useState<DOMRect>(
    new DOMRect(-1000, -1000, 0, 0)
  )
  React.useEffect(() => {
    const onMouseMove = (event: PointerEvent) => {
      if (isFrozen) return
      setDomRect(new DOMRect(event.clientX, event.clientY, 0, 0))
    }
    document.addEventListener('pointermove', onMouseMove)
    return () => document.removeEventListener('pointermove', onMouseMove)
  }, [isFrozen])

  const [popperElement, setPopperElement] =
    React.useState<HTMLDivElement | null>(null)
  const virtualElement = React.useMemo<VirtualElement>(
    () => ({ getBoundingClientRect: () => domRect }),
    [domRect]
  )

  const { styles, attributes } = usePopper(virtualElement, popperElement, {
    placement: 'left',
    modifiers: [
      { name: 'offset', options: { offset: [10, 20] } },
      {
        name: 'flip',
        options: {
          padding: 500,
          fallbackPlacements: ['top', 'right'],
          altBoundary: true,
        },
      },
    ],
  })

  return isVisible ? (
    <Draggable>
      <PopperContainer
        ref={setPopperElement}
        style={styles.popper}
        {...attributes.popper}>
        <VizTooltipContainer isFrozen={isFrozen}>
          {children}
        </VizTooltipContainer>
      </PopperContainer>
    </Draggable>
  ) : null
}
